/** Abwesenheiten: Urlaub beantragen, Krankmeldung, Resturlaub. */
import { api } from '../api.js';
import { cachedGet } from '../store.js';
import { $, errorText, esc, formValues, toast } from '../ui.js';
import type { AbsenceDTO } from '../../shared/types.js';
import type { VacationSummary } from '../../shared/vacation.js';
import { berlinDate, formatDateDe, formatNumberDe } from '../../shared/time.js';

export const STATUS_LABEL: Record<string, string> = {
  beantragt: 'beantragt', genehmigt: 'genehmigt', abgelehnt: 'abgelehnt', gemeldet: 'gemeldet', storniert: 'zurückgezogen',
};

export function period(a: Pick<AbsenceDTO, 'startDate' | 'endDate' | 'halfDay'>): string {
  if (a.startDate === a.endDate) return `${formatDateDe(a.startDate)}${a.halfDay ? ' (halber Tag)' : ''}`;
  return `${formatDateDe(a.startDate)} – ${formatDateDe(a.endDate)}`;
}

const days = (n: number) => `${formatNumberDe(n, n % 1 ? 1 : 0)} ${n === 1 ? 'Tag' : 'Tage'}`;

export async function renderAbsences(el: HTMLElement, year: number): Promise<void> {
  el.innerHTML = '<h1>Abwesenheit</h1><p class="muted">Lädt …</p>';
  const r = await cachedGet<{ absences: AbsenceDTO[]; summary: VacationSummary }>(`absences:${year}`, `/absences?year=${year}`);
  const today = berlinDate(Date.now());
  const s = r.data?.summary;
  el.innerHTML = `
    <div class="month-nav">
      <a class="btn icon" href="#/abwesenheit/${year - 1}" aria-label="Vorjahr">‹</a>
      <h1>Abwesenheit ${year}</h1>
      <a class="btn icon" href="#/abwesenheit/${year + 1}" aria-label="Folgejahr">›</a>
    </div>
    ${r.offline ? '<div class="alert">Offline – Anzeige aus dem Speicher. Anträge sind nur mit Verbindung möglich.</div>' : ''}
    ${s ? `<div class="vacation">
      <div class="vac-main"><span class="big">${formatNumberDe(s.remaining, s.remaining % 1 ? 1 : 0)}</span><span> Urlaubstage übrig</span></div>
      <div class="muted small">Anspruch ${days(s.entitlement)} · genehmigt ${days(s.approved)}${s.pending ? ` · beantragt ${days(s.pending)}` : ''}${s.sickDays ? ` · krank ${days(s.sickDays)}` : ''}</div>
    </div>` : ''}
    <details class="card" ${r.data?.absences.length ? '' : 'open'}>
      <summary><strong>Urlaub beantragen oder krank melden</strong></summary>
      <form id="abs" class="stack">
        <div class="seg">
          <label><input type="radio" name="type" value="urlaub" checked> Urlaub</label>
          <label><input type="radio" name="type" value="krank"> Krank</label>
        </div>
        <div class="row2">
          <label>Von<input type="date" name="startDate" value="${today}" required></label>
          <label>Bis<input type="date" name="endDate" value="${today}" required></label>
        </div>
        <label class="check"><input type="checkbox" name="halfDay" value="1"> Nur halber Tag (Von = Bis)</label>
        <label>Notiz (optional)<input name="note" maxlength="500"></label>
        <p class="muted small" id="krank-hint" hidden>Bitte zusätzlich wie gewohnt telefonisch Bescheid geben. Eine AU-Bescheinigung wird von der Krankenkasse elektronisch abgerufen – Diagnosen gehören nicht hierher.</p>
        <button class="btn primary big">Absenden</button>
      </form>
    </details>
    <h2>Meine Einträge</h2>
    ${r.data?.absences.length ? `<ul class="abs-list">${r.data.absences
      .map((a) => `<li class="st-${a.status}">
        <div><strong>${a.type === 'urlaub' ? 'Urlaub' : 'Krank'}</strong> · ${esc(period(a))}</div>
        <div class="muted small">${days(a.days)} · ${esc(STATUS_LABEL[a.status])}${a.note ? ` · ${esc(a.note)}` : ''}</div>
        ${a.status === 'beantragt' ? `<button class="btn small" data-cancel="${esc(a.id)}">Zurückziehen</button>` : ''}
      </li>`)
      .join('')}</ul>` : '<p class="muted">Keine Abwesenheiten in diesem Jahr.</p>'}
  `;
  const form = $('#abs', el) as HTMLFormElement;
  form.addEventListener('change', () => {
    $('#krank-hint', el).hidden = formValues(form).type !== 'krank';
    const v = formValues(form);
    const end = form.elements.namedItem('endDate') as HTMLInputElement;
    if (v.endDate < v.startDate) end.value = v.startDate;
  });
  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const v = formValues(form);
    try {
      await api('/absences', {
        method: 'POST',
        body: { type: v.type, startDate: v.startDate, endDate: v.endDate, halfDay: v.halfDay === '1', note: v.note },
      });
      toast(v.type === 'urlaub' ? 'Urlaubsantrag gesendet.' : 'Krankmeldung gespeichert. Gute Besserung!');
      void renderAbsences(el, year);
    } catch (err) {
      toast(errorText(err), 'error');
    }
  });
  el.querySelectorAll<HTMLButtonElement>('[data-cancel]').forEach((b) =>
    b.addEventListener('click', async () => {
      if (!confirm('Antrag zurückziehen?')) return;
      try {
        await api(`/absences/${b.dataset.cancel}`, { method: 'DELETE' });
        void renderAbsences(el, year);
      } catch (err) {
        toast(errorText(err), 'error');
      }
    }),
  );
}
